import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-[#1a1012]/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-black/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-black/10">
            <h2 className="text-2xl font-bold text-[#1a1012]">Privacy Policy</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-[#fbeceb] transition-colors text-[#5b4d4f] hover:text-[#ba1609]"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="text-[#5b4d4f] space-y-6">
              {/* Last Updated */}
              <div className="text-center">
                <p className="text-sm text-[#5b4d4f]">Last updated: January 2025</p>
              </div>

              {/* Introduction */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Introduction</h3>
                <p className="leading-relaxed">
                  At <span style={{ fontFamily: 'Playfair Display, serif' }}>Lasmari Chauffeurs</span>, we are committed to protecting your privacy and ensuring the security of your personal information.
                  This Privacy Policy explains how we collect, use, process, and protect your information when you use our luxury
                  transportation services in Corfu, Greece.
                </p>
              </section>

              {/* Information We Collect */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Information We Collect</h3>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-[#1a1012] mb-2">Booking Information</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Full name and contact details (email, phone number)</li>
                      <li>Pickup and drop-off locations</li>
                      <li>Travel dates and times</li>
                      <li>Number of passengers and luggage requirements</li>
                      <li>Special requests or preferences</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-[#1a1012] mb-2">Technical Information</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>IP address and device information</li>
                      <li>Browser type and version</li>
                      <li>Location data (with your consent) for pickup services</li>
                      <li>Website usage analytics</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* How We Use Information */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">How We Use Your Information</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>To provide and manage your transportation bookings</li>
                  <li>To communicate with you about your reservations</li>
                  <li>To improve our services and customer experience</li>
                  <li>To send booking confirmations and updates</li>
                  <li>To comply with legal obligations</li>
                  <li>To ensure the safety and security of our services</li>
                </ul>
              </section>

              {/* Information Sharing */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Information Sharing</h3>
                <p className="leading-relaxed mb-3">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>With our drivers to facilitate your transportation service</li>
                  <li>With payment processors to handle transactions</li>
                  <li>When required by law or to protect our legal rights</li>
                  <li>In case of business transfer or merger (with prior notice)</li>
                </ul>
              </section>

              {/* Data Security */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Data Security</h3>
                <p className="leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal data against unauthorized
                  access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security audits.
                </p>
              </section>

              {/* Your Rights */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Your Rights (GDPR)</h3>
                <p className="leading-relaxed mb-3">Under GDPR, you have the right to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate information</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing of your data</li>
                  <li>Data portability</li>
                  <li>Withdraw consent at any time</li>
                </ul>
              </section>

              {/* Cookies */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Cookies and Tracking</h3>
                <p className="leading-relaxed">
                  We use cookies and similar technologies to enhance your browsing experience, analyze website traffic,
                  and provide personalized content. You can control cookie settings through your browser preferences.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Contact Us</h3>
                <p className="leading-relaxed">
                  If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
                </p>
                <div className="mt-3 space-y-1">
                  <p><strong className="text-[#1a1012]">Email:</strong> contact@lasmari.gr</p>
                  <p><strong className="text-[#1a1012]">Phone:</strong> +30 2663098633</p>
                  <p><strong className="text-[#1a1012]">Address:</strong> Apraos, North Corfu, Greece</p>
                </div>
              </section>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PrivacyPolicyModal;